/**
 * BookDemoModal — landing-page Book a Demo widget.
 *
 * 3-step flow modeled on patriotanalytics.ai's booking widget:
 *   Step 1 (schedule): timezone dropdown + business-day grid + 30-min slot grid
 *   Step 2 (details) : name, email, company, use case
 *   Step 3 (success) : confirmed slot + "check your email" panel
 *
 * All slots are fetched from `GET /api/demo/slots?tz=<IANA>&days=14`. The
 * booking POSTs `slot_start_utc` (already ISO-UTC from the server payload)
 * plus the prospect's chosen timezone.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Check, Loader2, Mail, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated IANA list — covers ~95% of demo prospects. Keep short so the
// dropdown stays scannable.
const TIMEZONES = [
  { value: 'America/New_York',   label: 'New York (Eastern · ET)' },
  { value: 'America/Chicago',    label: 'Chicago (Central · CT)' },
  { value: 'America/Denver',     label: 'Denver (Mountain · MT)' },
  { value: 'America/Phoenix',    label: 'Phoenix (Arizona · MST)' },
  { value: 'America/Los_Angeles',label: 'Los Angeles (Pacific · PT)' },
  { value: 'America/Toronto',    label: 'Toronto (ET)' },
  { value: 'America/Sao_Paulo',  label: 'São Paulo (BRT)' },
  { value: 'Europe/London',      label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',       label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin',      label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam',   label: 'Amsterdam (CET/CEST)' },
  { value: 'Africa/Johannesburg',label: 'Johannesburg (SAST)' },
  { value: 'Asia/Dubai',         label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata',       label: 'Mumbai / Delhi (IST)' },
  { value: 'Asia/Singapore',     label: 'Singapore (SGT)' },
  { value: 'Asia/Hong_Kong',     label: 'Hong Kong (HKT)' },
  { value: 'Asia/Tokyo',         label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney',   label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland',   label: 'Auckland (NZST/NZDT)' },
  { value: 'UTC',                label: 'UTC (Coordinated Universal Time)' },
];

const detectBrowserTz = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONES.some((t) => t.value === tz)) return tz;
    return 'America/New_York';
  } catch {
    return 'America/New_York';
  }
};

const BookDemoModal = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1); // 1 = schedule, 2 = details, 3 = success
  const [tz, setTz] = useState(detectBrowserTz());
  const [slotsPayload, setSlotsPayload] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);

  const [selectedDateIso, setSelectedDateIso] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // { start_utc, end_utc, local_time_display }

  const [form, setForm] = useState({ full_name: '', email: '', company: '', use_case: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Reset every time the modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedSlot(null);
      setConfirmation(null);
      setForm({ full_name: '', email: '', company: '', use_case: '' });
    }
  }, [open]);

  // Fetch slots whenever the modal opens or the timezone changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      setSlotsError(null);
      try {
        const r = await axios.get(`${API_BASE}/demo/slots`, {
          params: { tz, days: 14, session_type: 'product_demo' },
        });
        if (cancelled) return;
        setSlotsPayload(r.data);
        // Preselect first date with any available slot
        const firstDay = (r.data?.days || []).find((d) => d.slots.some((s) => s.available));
        setSelectedDateIso(firstDay?.local_date_iso || null);
        setSelectedSlot(null);
      } catch (e) {
        if (cancelled) return;
        setSlotsError('Could not load available times. Please try again.');
        setSlotsPayload(null);
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, tz]);

  const selectedDay = useMemo(
    () => (slotsPayload?.days || []).find((d) => d.local_date_iso === selectedDateIso) || null,
    [slotsPayload, selectedDateIso]
  );

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const canContinueToDetails = !!selectedSlot;
  const canSubmit =
    form.full_name.trim().length >= 1 &&
    isValidEmail(form.email.trim()) &&
    !submitting &&
    !!selectedSlot;

  const submitBooking = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        slot_start_utc: selectedSlot.start_utc,
        timezone: tz,
        session_type: 'product_demo',
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        use_case: form.use_case.trim() || null,
      };
      const r = await axios.post(`${API_BASE}/demo/bookings`, payload);
      setConfirmation(r.data);
      setStep(3);
      toast.success('Your demo is booked — check your inbox for the calendar invite!');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error('That slot was just taken. Please pick another.');
        // Reload slots so the UI stays consistent
        setSelectedSlot(null);
        setStep(1);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Booking failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-[#0F1730] max-h-[90vh] overflow-y-auto"
        data-testid="book-demo-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl flex items-center gap-2">
            <Calendar className="h-5 w-5 text-red-400" />
            Book a Product Demo
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            30 minutes with the dotbox.ai team. Confirmation, calendar invite, and video link arrive instantly by email.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — schedule */}
        {step === 1 && (
          <div className="space-y-6" data-testid="book-demo-step-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Globe className="h-3 w-3" /> Your timezone
                </Label>
                <select
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                  className="w-full bg-[#0A1024] border border-[#1F2A4D] rounded-md px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-red-500"
                  data-testid="book-demo-timezone"
                >
                  {TIMEZONES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Badge variant="outline" className="border-red-600/40 text-red-300 bg-red-950/30 px-3 py-1">
                  <Clock className="h-3 w-3 mr-1.5" /> Product Demo · 30 min
                </Badge>
              </div>
            </div>

            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider mb-2 block">1 — Select a date</Label>
              {slotsLoading && (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-6" data-testid="book-demo-loading-slots">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading available times…
                </div>
              )}
              {slotsError && (
                <div className="text-sm text-rose-400 py-3" data-testid="book-demo-slots-error">{slotsError}</div>
              )}
              {!slotsLoading && !slotsError && slotsPayload && (
                <>
                  {(slotsPayload.days || []).length === 0 ? (
                    <div className="text-sm text-slate-400" data-testid="book-demo-no-slots">
                      No open slots in the next {slotsPayload.min_lead_days}+ business days. Please try a different timezone.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="book-demo-dates-container">
                      {slotsPayload.days.map((d) => {
                        const active = d.local_date_iso === selectedDateIso;
                        return (
                          <button
                            key={d.local_date_iso}
                            type="button"
                            onClick={() => { setSelectedDateIso(d.local_date_iso); setSelectedSlot(null); }}
                            className={`px-3 py-2 rounded-md text-sm border transition ${
                              active
                                ? 'bg-red-600/20 border-red-500 text-white'
                                : 'bg-[#0A1024] border-[#1F2A4D] text-slate-200 hover:border-red-600/40'
                            }`}
                            data-testid={`book-demo-date-${d.local_date_iso}`}
                          >
                            {d.local_date_display}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedDay && (
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider mb-2 block">
                  2 — Select a time ({tz.replace('_', ' ')})
                </Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="book-demo-slot-grid">
                  {selectedDay.slots.map((s) => {
                    const active = selectedSlot?.start_utc === s.start_utc;
                    return (
                      <button
                        key={s.start_utc}
                        type="button"
                        disabled={!s.available}
                        onClick={() => setSelectedSlot(s)}
                        className={`px-3 py-2 rounded-md text-xs font-medium border transition ${
                          active
                            ? 'bg-red-600 border-red-500 text-white'
                            : s.available
                              ? 'bg-[#0A1024] border-[#1F2A4D] text-slate-200 hover:border-red-600/40'
                              : 'bg-[#0A1024]/50 border-[#1F2A4D] text-slate-600 line-through cursor-not-allowed'
                        }`}
                        data-testid={`book-demo-slot-${s.start_utc}`}
                      >
                        {s.local_time_display}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={() => setStep(2)}
                disabled={!canContinueToDetails}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="book-demo-continue-button"
              >
                Continue to details →
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — details */}
        {step === 2 && selectedSlot && (
          <div className="space-y-4" data-testid="book-demo-step-2">
            <div className="bg-[#0A1024] border border-red-600/40 rounded-md px-4 py-3 flex items-center gap-3">
              <Check className="h-4 w-4 text-emerald-400 shrink-0" />
              <div className="text-sm">
                <div className="text-slate-200">{selectedDay?.local_date_display} · {selectedSlot.local_time_display}</div>
                <div className="text-xs text-slate-500">{tz}</div>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="ml-auto text-xs text-red-300 hover:text-red-200 underline"
                data-testid="book-demo-change-slot"
              >
                Change
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider mb-1.5">Full name *</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Alex Chen"
                  className="bg-[#0A1024] border-[#1F2A4D] text-slate-100"
                  data-testid="book-demo-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider mb-1.5">Work email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="alex@company.com"
                  className="bg-[#0A1024] border-[#1F2A4D] text-slate-100"
                  data-testid="book-demo-email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider mb-1.5">Company</Label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Acme Corp"
                className="bg-[#0A1024] border-[#1F2A4D] text-slate-100"
                data-testid="book-demo-company-input"
              />
            </div>

            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider mb-1.5">
                What would you like to see? (optional)
              </Label>
              <Textarea
                value={form.use_case}
                onChange={(e) => setForm({ ...form, use_case: e.target.value })}
                placeholder="e.g. Competitive intelligence for our sales team, or how Signals + Agents work end-to-end."
                rows={3}
                className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 resize-none"
                data-testid="book-demo-usecase-input"
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-[#1F2A4D] bg-[#0A1024] text-slate-200 hover:text-white"
                data-testid="book-demo-back-button"
              >
                ← Back
              </Button>
              <Button
                onClick={submitBooking}
                disabled={!canSubmit}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="book-demo-confirm-button"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Confirming…</>
                ) : (
                  'Confirm Product Demo'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — success */}
        {step === 3 && confirmation && (
          <div className="space-y-4 py-2" data-testid="book-demo-step-3">
            <div className="flex flex-col items-center text-center py-6">
              <div className="h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
                <Check className="h-7 w-7 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-1">Your demo is confirmed</h3>
              <p className="text-sm text-slate-400 max-w-md">
                We&apos;ve sent a calendar invite to <strong className="text-slate-200">{form.email}</strong>. Just open the .ics attachment to add it to your calendar.
              </p>
            </div>
            <div className="bg-[#0A1024] border border-[#1F2A4D] rounded-md p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-red-400" />
                <span className="text-slate-100 font-medium">{confirmation.local_start_display}</span>
                <span className="text-slate-500">→ {confirmation.local_end_display}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Globe className="h-3.5 w-3.5" /> {confirmation.timezone}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400 break-all">
                <Mail className="h-3.5 w-3.5 shrink-0" /> Video link and .ics attachment sent to your inbox
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="book-demo-close-button"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookDemoModal;
